/**
 * ADR-084: decision-модели (System One: Clef/Jev/Decision-1/…) как единый
 * примитив — `state` (текст/JSON) + типизированные вопросы → вероятности за
 * один forward-pass. Здесь только контракты без бизнес-логики: сами адаптеры и
 * вызовы живут в core (`providers/decision.ts`, `GatewayProvider.decide`).
 */
import { z } from 'zod';
/** Границы числа вариантов для `choice`/`score` (System One-контракт). */
export const DECISION_CRITERIA_MIN = 2;
export const DECISION_CRITERIA_MAX = 26;
// --- Zod-схемы (валидация запроса/ответа) ---
const decisionNoulQuestionSchema = z.object({
    type: z.literal('noul'),
    instructions: z.string().optional(),
    criteria: z
        .object({ true: z.string().optional(), false: z.string().optional() })
        .optional(),
});
const decisionChoiceQuestionSchema = z.object({
    type: z.literal('choice'),
    instructions: z.string(),
    criteria: z.record(z.string(), z.string().nullable()),
});
const decisionScoreQuestionSchema = z.object({
    type: z.literal('score'),
    instructions: z.string(),
    criteria: z.array(z.string()),
});
/**
 * ADR-084: универсальная схема вопроса. Границы `choice`/`score` (2..26)
 * проверяются отдельно — иначе `discriminatedUnion` не принимает
 * `ZodEffects`-обёртки внутри вариантов.
 */
export const decisionQuestionSchema = z
    .discriminatedUnion('type', [
    decisionNoulQuestionSchema,
    decisionChoiceQuestionSchema,
    decisionScoreQuestionSchema,
])
    .superRefine((question, ctx) => {
    if (question.type === 'choice') {
        const count = Object.keys(question.criteria).length;
        if (count < DECISION_CRITERIA_MIN || count > DECISION_CRITERIA_MAX) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `choice.criteria: от ${DECISION_CRITERIA_MIN} до ${DECISION_CRITERIA_MAX} вариантов`,
            });
        }
    }
    if (question.type === 'score') {
        const count = question.criteria.length;
        if (count < DECISION_CRITERIA_MIN || count > DECISION_CRITERIA_MAX) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `score.criteria: от ${DECISION_CRITERIA_MIN} до ${DECISION_CRITERIA_MAX} позиций`,
            });
        }
    }
});
export const decisionNoulAnswerSchema = z.object({
    type: z.literal('noul'),
    noul: z.number(),
});
export const decisionChoiceAnswerSchema = z.object({
    type: z.literal('choice'),
    choice: z.string(),
    confidence: z.number(),
    probabilities: z.record(z.string(), z.number()),
});
export const decisionScoreAnswerSchema = z.object({
    type: z.literal('score'),
    score: z.number(),
    confidence: z.number(),
    probabilities: z.record(z.string(), z.number()),
    legend: z.record(z.string(), z.string()).optional(),
});
/** ADR-084: схема ответа decision-модели. */
export const decisionAnswerSchema = z.discriminatedUnion('type', [
    decisionNoulAnswerSchema,
    decisionChoiceAnswerSchema,
    decisionScoreAnswerSchema,
]);
export const decisionRequestSchema = z.object({
    model: z.string(),
    state: z.unknown(),
    questions: z.record(z.string(), decisionQuestionSchema),
    /** ADR-084/PROP-085: медиа-вложения (изображения/видео) — опциональны. */
    images: z.array(z.string()).optional(),
    videos: z.array(z.string()).optional(),
});
export const decisionResponseSchema = z.object({
    id: z.string().optional(),
    model: z.string().optional(),
    answers: z.record(z.string(), decisionAnswerSchema),
    usage: z
        .object({ input_tokens: z.number(), output_tokens: z.number() })
        .optional(),
});
